import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Color, Grid, Type } from '../../app/StyleGuide';
import { selectStep, setShowTrainingCompletedModal, useCompleteStep, useSteps } from './StepsContext';
import { PrimaryButton } from '../../components/Buttons';
import ErrorMessage from '../../components/ErrorMessage';
import WhiteLabel from '../../filters/WhiteLabel';
import capitalizeFilter from '../../filters/capitalize-filter';
import { sanitizeHTML } from '../../utils/sanitizeHTML';
import AssessmentStepProxy from './AssessmentStepProxy';
import CompleteStepButton from './CompleteStepButton';
import CompleteStepButtonContainer from './CompleteStepButtonContainer';
import DocumentViewerStep from './DocumentViewerStep';
import DownloadStep from './DownloadStep';
import FeedbackStep from './FeedbackStep';
import LinkStep from './LinkStep';
import PrepareEnrichStep from './PrepareEnrichStep';
import Section from './Section';
import StepHeader from './StepHeader';
import StepMenu from './StepMenu';
import UploadStep from './UploadStep';
import VideoStep from './VideoStep';

const Content = styled.article`
  height: 100vh;
  height: 100dvh;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  color: ${Color.Secondary._80};
  background-color: ${Color.Gray._05};

  .locked-step {
    color: ${Type.Color.dark};
    text-align: center;
    i {
      color: ${Color.Secondary._50};
      font-size: ${Grid._7};
    }
  }
`;

const ScrollableStepsContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  min-height: 0;
  overflow: auto;
`;

const StepDescriptionContent = styled.div`
  img {
    max-width: 100%;
  }
`;

const CompleteCourseButton = ({
  disabled,
  isWorkflow,
  dispatch,
}: {
  disabled: boolean;
  isWorkflow: boolean;
  dispatch: any;
}) => {
  const trainingType = isWorkflow ? WhiteLabel.labels.pathway : WhiteLabel.labels.course;

  return (
    <CompleteStepButtonContainer>
      <PrimaryButton
        data-qa-hook="completeCourseButton"
        onClick={() => setShowTrainingCompletedModal(dispatch, { showTrainingCompletedModal: true })}
        disabled={disabled}
      >
        Complete {capitalizeFilter(trainingType)}
      </PrimaryButton>
    </CompleteStepButtonContainer>
  );
};

const ErrorMessageButton = ({ stepType }: { stepType: string }) => {
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const msg = {
    Link: 'Please visit the link in order to complete this ' + WhiteLabel.labels.step,
    MyWSBLink: 'Please visit the link in order to complete this ' + WhiteLabel.labels.step,
    Upload: 'Please upload the requested file in order to complete this ' + WhiteLabel.labels.step,
    Download: 'Please download the file in order to complete this ' + WhiteLabel.labels.step,
  };

  return (
    <>
      {showErrorMessage && (
        <ErrorMessage data-qa-hook="completeStepButtonError">{msg[stepType as keyof typeof msg]}</ErrorMessage>
      )}
      <PrimaryButton data-qa-hook="completeStepButtonActionNeeded" onClick={() => setShowErrorMessage(true)}>
        Next
      </PrimaryButton>
    </>
  );
};

export default function StepContent() {
  const headerRef = useRef<HTMLDivElement>(null);
  const scrollableRef = useRef<HTMLDivElement>(null);

  const { completeStep } = useCompleteStep();

  const [
    {
      isWorkflow,
      training,
      lastStepIndex,
      currentStep,
      currentStepIndex,
      currentCourseIndex,
      currentCourse,
      setShowLockedStep,
      currentStepIsIncomplete,
      currentStepIsInProgress,
      numCompletedSteps,
      completeModalShown,
    },
    dispatch,
  ] = useSteps();

  useLayoutEffect(() => {
    if (scrollableRef.current) {
      scrollableRef.current.scrollTop = 0;
    }
  }, [currentStep]);

  useEffect(() => {
    if (currentStep.task?._type === 'Text' || currentStep.task?._type === 'DocumentViewer') completeStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  if (setShowLockedStep) {
    return (
      <Content>
        <StepHeader ref={headerRef} />
        <Section className="locked-step">
          <p>
            <i className="fa fa-lock"></i>
          </p>
          <h3>This {WhiteLabel.labels.step} is locked</h3>
          <p className="text-color-medium">
            This {WhiteLabel.labels.course} will be unlocked upon completion of previous {WhiteLabel.labels.course}.
          </p>
        </Section>
      </Content>
    );
  } else {
    const stepIsComplete = currentStep && currentStep.status === 'complete';
    const isLastStep = Number(currentStepIndex) + 1 === currentCourse?.tasks?.length;
    const courseIsFinished = isWorkflow
      ? currentCourse?.progress?.percent === 100
      : numCompletedSteps / currentCourse?.tasks?.length === 1;
    const isLastStepToBeCompleted =
      numCompletedSteps + 1 === currentCourse?.tasks?.length && currentStepIndex === lastStepIndex;

    const isLastCourse =
      isWorkflow &&
      training.courses.every(
        (course: { progress?: { _type?: string } }, i: number) =>
          i === Number(currentCourseIndex) || course.progress?._type === 'Finished'
      );
    const isNotLastStepInTraining = isWorkflow ? (isLastCourse ? !isLastStep : true) : !isLastStep;

    const isVideo = currentStep.task._type === 'Video' || currentStep.task._type === 'ThirdPartyVideo';
    const isAssessment = currentStep.task._type === 'Assessment';
    const isPrepareEnrich = currentStep.task._type === 'PrepareEnrich';
    const isFeedback = currentStep.task._type === 'Feedback';
    const isText = currentStep.task._type === 'Text';
    const isDocumentViewer = currentStep.task._type === 'DocumentViewer';
    const isDownload = currentStep.task._type === 'Download';
    const isUpload = currentStep.task._type === 'Upload';
    const isLink = currentStep.task._type === 'Link' || currentStep.task._type === 'MyWSBLink';
    const requiresAction =
      currentStep.task._type === 'Link' ||
      currentStep.task._type === 'Upload' ||
      currentStep.task._type === 'Download' ||
      currentStep.task._type === 'MyWSBLink';
    const stepDescription = currentStep.task && currentStep.task.taskDescription;

    // These steps have their own content with rules for determining completion
    const isSelfRenderedStep = isVideo || isAssessment || isPrepareEnrich || isDocumentViewer;

    const showStepDescription = !isSelfRenderedStep && !isUpload && stepDescription;
    const showStepMenuArea = !isSelfRenderedStep && !isFeedback;
    const hasScrollableContent =
      isVideo ||
      isAssessment ||
      isPrepareEnrich ||
      isUpload ||
      showStepDescription ||
      isDownload ||
      isLink ||
      isFeedback ||
      showStepMenuArea;
    const showIncompleteStepActions =
      !isSelfRenderedStep &&
      !isFeedback &&
      currentStepIsIncomplete &&
      isNotLastStepInTraining &&
      !isLastStepToBeCompleted;
    const showCompleteCourseButton =
      (!isWorkflow || isLastCourse) && (courseIsFinished || isLastStepToBeCompleted) && !completeModalShown;
    const showNextStepButton =
      stepIsComplete && isNotLastStepInTraining && !isLastStepToBeCompleted && !showCompleteCourseButton;

    const goToNextStep = () => {
      selectStep(dispatch, { stepIndex: Number(currentStepIndex) + 1, courseIndex: currentCourseIndex });
    };

    const goToNextCourse = () => {
      const current = Number(currentCourseIndex);
      const isIncomplete = (course: { progress?: { _type?: string } }) => course.progress?._type !== 'Finished';

      const forward = training.courses.findIndex(
        (course: { progress?: { _type?: string } }, i: number) => i > current && isIncomplete(course)
      );
      const next = forward >= 0 ? forward : training.courses.findIndex(isIncomplete);

      const nextCourse = training.courses[next];
      const firstIncompleteStepIndex = nextCourse.tasks.findIndex(
        (task: { status: string }) => task.status !== 'complete'
      );
      const stepIndex = firstIncompleteStepIndex >= 0 ? firstIncompleteStepIndex : 0;

      selectStep(dispatch, { stepIndex, courseIndex: next });
    };

    return (
      <Content>
        <StepHeader ref={headerRef} />

        {hasScrollableContent && (
          <ScrollableStepsContent ref={scrollableRef}>
            {isVideo && <VideoStep key={currentStep.id.id} />}
            {isAssessment && <AssessmentStepProxy />}
            {isPrepareEnrich && <PrepareEnrichStep />}
            {isUpload && <UploadStep />}
            {showStepDescription && (
              <Section>
                <StepDescriptionContent
                  data-qa-hook="stepDescription"
                  dangerouslySetInnerHTML={{ __html: sanitizeHTML(stepDescription) }}
                />
              </Section>
            )}
            {isDownload && <DownloadStep />}
            {isLink && <LinkStep />}
            {isFeedback && <FeedbackStep />}

            {showStepMenuArea && <StepMenu />}
          </ScrollableStepsContent>
        )}
        {isDocumentViewer && <DocumentViewerStep />}
        {showIncompleteStepActions && (
          <CompleteStepButtonContainer>
            {isText || (requiresAction && currentStepIsInProgress) ? (
              <CompleteStepButton />
            ) : (
              requiresAction && !currentStepIsInProgress && <ErrorMessageButton stepType={currentStep.task?._type} />
            )}
          </CompleteStepButtonContainer>
        )}
        {showNextStepButton && (
          <CompleteStepButtonContainer>
            {isWorkflow && isLastStep && !isLastCourse ? (
              <PrimaryButton onClick={() => goToNextCourse()} disabled={!courseIsFinished}>
                Next Course
              </PrimaryButton>
            ) : (
              <PrimaryButton onClick={() => goToNextStep()}>Next</PrimaryButton>
            )}
          </CompleteStepButtonContainer>
        )}
        {showCompleteCourseButton && (
          <CompleteCourseButton isWorkflow={isWorkflow} dispatch={dispatch} disabled={!courseIsFinished} />
        )}
      </Content>
    );
  }
}
